import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { editCopyByReplaceMultipleStrings, pluralize } from '../../../../../components/utilities/copyFunctions';
import {
  getAdults,
  getArrival
} from '../../../../../store/componentStores/ManageReservation/manageReservation.selectors';
import {
  getAditionalPackagesFromAddApackage,
  getPackageTotalWithAdditionalPackages,
  packagesWithAmountFromReservationData
} from '../../../../../store/reducers';
import { DATE_FORMATS } from '../../../../../utilities/constants';
import { getDateMonthDayFormat } from '../../../../../utilities/dates';
import { GwDatesWrapper } from '../../../../_internal_date_/gwDatesWrapper';
import PackagesSummary from '../../../../CostSummary/PackagesSummary';
import { getNormalizedNumber, getPackageAmount } from '../../../../CostSummary/utilities/functions';
import { Modal } from '../../../../Modal';
import { Flex, Text } from '../../../../ui-kit';
import CheckIcon from '../../../../ui-kit/icons/CheckedIcon';
import CloseIcon from '../../../../ui-kit/icons/Close';
import theme from '../../../../ui-kit/themes/theme';
import {
  AppliedContainer,
  CloseButton,
  ContentContainer,
  InfoColumn,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
  PassDetailsRow,
  PassTaxesContent,
  PassTaxesTextLine,
  SeasonPassRow,
  SummaryInfoRow,
  TotalRow
} from './styles';
import * as COPY from './utilities/copy';

const propTypes = {
  opened: PropTypes.bool,
  onClose: PropTypes.func,
  arrivalDate: PropTypes.string,
  adults: PropTypes.number,
  reservationDetails: PropTypes.object,
  packages: PropTypes.array,
  packageTotal: PropTypes.number,
  additionalPackages: PropTypes.array,
  additionalPackageRenderer: PropTypes.func
};

export const SeasonPassDayPassSummary = ({
  opened,
  onClose,
  arrivalDate,
  adults,
  reservationDetails,
  packages,
  packageTotal,
  additionalPackages
}) => {
  const passCount = adults;
  const validDate = GwDatesWrapper.format(arrivalDate, DATE_FORMATS.shortMonthDayNumber);
  const taxes = reservationDetails.data?.costSummary?.taxesAndFees?.TAX || 0;
  const seasonPassId = reservationDetails.data.seasonPass.id;
  const totalCost = reservationDetails.data.costSummary.total;
  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.sm})`
  });

  const modalContentRef = useCallback(node => {
    if (node) node.tabIndex = 0;
  }, []);

  const SEASON_PASS_TYPE_LABELS = {
    anytime: COPY.ANYTIME,
    weekday: COPY.WEEKDAY
  };

  const passesText = editCopyByReplaceMultipleStrings(COPY.FULL_DAY_PASSES_COUNT, {
    '<COUNT>': passCount,
    '<PASS>': pluralize('Pass', passCount, 'es')
  });

  const rawSeasonPassName = reservationDetails?.data?.seasonPass?.name;
  const seasonPassNameText = editCopyByReplaceMultipleStrings(COPY.SEASON_PASS_NAME, {
    '<SEASON_PASS_NAME>': SEASON_PASS_TYPE_LABELS[rawSeasonPassName] ?? rawSeasonPassName,
    '<SEASON_PASS_ID>': seasonPassId
  });

  return (
    <Modal
      isOpen={opened}
      onRequestClose={onClose}
      padding={0}
      width={isMobile ? '85%' : '466px'}
      contentRef={modalContentRef}
      contentLabel={COPY.DAY_PASS_SUMMARY_TITLE}>
      <Flex flexDirection="column">
        <ModalHeader>
          <ModalTitle>{COPY.DAY_PASS_SUMMARY_TITLE}</ModalTitle>
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            <Flex alignItems="center">
              <CloseIcon color={theme.colors.preciousStoneBlue[100]} />
            </Flex>
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <ContentContainer>
            <SummaryInfoRow>
              <InfoColumn>
                <Text fontSize={0} color="deepLakeBlue.80" fontWeight="medium" mb={2}>
                  {COPY.NUMBER_OF_DAY_PASSES}
                </Text>
                <Text fontSize={4} color="deepLakeBlue.100" fontWeight="bold">
                  {passCount}
                </Text>
              </InfoColumn>
              <InfoColumn>
                <Text fontSize={0} color="deepLakeBlue.80" fontWeight="medium" mb={2}>
                  {COPY.VALID_ON}
                </Text>
                <Text fontSize={4} color="deepLakeBlue.100" fontWeight="bold">
                  {getDateMonthDayFormat(validDate)}
                </Text>
              </InfoColumn>
            </SummaryInfoRow>
            <ModalBody>
              <PassDetailsRow>
                <Text fontSize={1} color="deepLakeBlue.100" fontWeight="bold">
                  {COPY.FULL_DAY_RETAIL_WATER_PARK_PASS}
                </Text>
                <Text fontSize={1} color="deepLakeBlue.100" fontWeight="bold">
                  {COPY.ZERO_AMOUNT}
                </Text>
              </PassDetailsRow>
              <Text fontSize={1} color="deepLakeBlue.80" fontWeight="medium" mb={2}>
                {passesText}
              </Text>
              {/* Season Pass Applied */}
              <SeasonPassRow>
                <Text fontSize={1} color="deepForestGreen.100" fontWeight="semibold">
                  {seasonPassNameText}
                </Text>
                <AppliedContainer>
                  <CheckIcon color={theme.colors.successGreen} size="16px" />
                  <Text fontSize={1} color="deepForestGreen.100" fontWeight="semibold">
                    {COPY.APPLIED}
                  </Text>
                </AppliedContainer>
              </SeasonPassRow>
              {/* Taxes details */}
              <PassTaxesContent>
                <PassTaxesTextLine>{COPY.TAXES}</PassTaxesTextLine>
                <PassTaxesTextLine>${taxes.toFixed(2)}</PassTaxesTextLine>
              </PassTaxesContent>
              {/* Package details */}
              {packages.length > 0 && (
                <PackagesSummary
                  showTotal={false}
                  showTitle={false}
                  packages={packages}
                  packageTotal={packageTotal}
                  additionalPackages={additionalPackages}
                  showNotification={false}
                  getNormalizedNumber={getNormalizedNumber}
                  getPackageAmount={getPackageAmount}
                />
              )}

              {/* Total */}
              <TotalRow>
                <Text fontSize={3} color="deepLakeBlue.100" fontWeight="bold">
                  {COPY.TOTAL}
                </Text>
                <Text fontSize={3} color="deepLakeBlue.100" fontWeight="bold">
                  {totalCost ? `$${totalCost.toFixed(2)}` : COPY.ZERO_AMOUNT}
                </Text>
              </TotalRow>
            </ModalBody>
          </ContentContainer>
        </ModalContent>
      </Flex>
    </Modal>
  );
};

SeasonPassDayPassSummary.propTypes = propTypes;
SeasonPassDayPassSummary.defaultProps = {
  opened: false
};

const mapStateToProps = state => {
  return {
    adults: getAdults(state.entities.manageReservationDetails),
    arrivalDate: getArrival(state.entities.manageReservationDetails),
    reservationDetails: state.entities.manageReservationDetails,
    packages: packagesWithAmountFromReservationData(state) ?? [],
    packageTotal: getPackageTotalWithAdditionalPackages(state),
    additionalPackages: getAditionalPackagesFromAddApackage(state) ?? []
  };
};

export default connect(mapStateToProps, null)(SeasonPassDayPassSummary);
